<template>
  <v-app>
    <GlobalModalDialog />

    <component :is="layout">
      <SubcontractorImpersonationBanner />
      <router-view :key="appRemountKey" />
    </component>

    <v-snackbar
      v-model="isSnackbarVisible"
      :timeout="timeout"
      :color="color"
      :location="location"
      :class="customClasses"
      :contentClass="elevationClass"
      :contentProps="snackbarContentProps"
      :data-status="status"
      data-cc="app-snackbar"
    >
      <div class="d-flex ga-2 align-center" :class="customClasses">
        <v-icon
          v-if="icon"
          :icon="icon.icon"
          :color="icon.color"
          class="ic-22"
        />

        <span v-html="message" />
      </div>

      <template v-if="hasSnackbarCloseBtn" #actions>
        <v-icon-btn
          v-if="hasSnackbarCloseBtn"
          variant="text"
          icon="$close"
          size="small"
          @click="closeSnackbar"
        />
      </template>
    </v-snackbar>

    <BMLanguageDialog
      v-if="isLanguageDialogOpen"
      @close="setLanguageDialogOpen(false)"
    />
  </v-app>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, watch } from 'vue';

import { useRoute, useRouter } from 'vue-router';

import GlobalModalDialog from '@/components/common/GlobalModalDialog.vue';
import BMLanguageDialog from '@/components/common/utilities/BMLanguageDialog.vue';

import { useImpersonationLifecycle } from '@/composables/impersonation';
import { useAppTheme } from '@/composables/useAppTheme';
import { useIntercom as useIntercomConfig } from '@/composables/useIntercom';
import { useLanguageSelector } from '@/composables/useLanguageSelector';
import { usePageTitle } from '@/composables/usePageTitle';
import { useSnackbar } from '@/composables/useSnackbar';
import SubcontractorImpersonationBanner from '@/contexts/depot/SubcontractorImpersonationBanner.vue';
import useAuthStore from '@/stores/auth';
import useNavigationStore from '@/stores/navigation';

defineOptions({
  name: 'BackOffice'
});

const { appRemountKey } = useImpersonationLifecycle();

usePageTitle();

// Libs
const router = useRouter();
const route = useRoute();

// Global Snackbar
const {
  isSnackbarVisible,
  hasSnackbarCloseBtn,
  timeout,
  message,
  status,
  color,
  elevationClass,
  location,
  customClasses,
  icon,
  closeSnackbar
} = useSnackbar();

const snackbarContentProps = computed(() => ({
  style: {
    marginInlineEnd: '80px',
    marginBlockEnd: '12px'
  }
}));

// Language Dialog
const { isLanguageDialogOpen, setLanguageDialogOpen } = useLanguageSelector();

// App Theme
useAppTheme();

// Set Intercom
const { intercomColor, intercomThemeMode, updateIntercomTheme } =
  useIntercomConfig();

const { setDocumentVisibility } = useNavigationStore();
const authStore = useAuthStore();
const isUserAuthenticated = computed(() => authStore.isUserAuthenticated);

// Map the string from meta to actual components
const layouts = {
  default: defineAsyncComponent(() => import('@/layouts/DefaultLayout.vue')),
  public: defineAsyncComponent(() => import('@/layouts/PublicLayout.vue'))
};

// Fallback to Default if no meta is defined
const layout = computed(() => {
  const layoutName = route.meta?.layout || 'default';
  return layouts[layoutName];
});

// Watchers
watch(isUserAuthenticated, (authenticationState) => {
  // Only redirect to login when the current route requires authentication.
  // Unguarded routes (e.g. design guide, prototypes) are accessible without
  // a session and must not be interrupted when auth state clears.
  if (!authenticationState && !route.meta?.unguarded) {
    router.push({ name: 'login' });
  }
});

watch(
  [intercomColor, intercomThemeMode],
  () => {
    updateIntercomTheme();
  },
  { immediate: true }
);

// Hooks
onMounted(() => {
  window.document.addEventListener(
    'visibilitychange',
    () => setDocumentVisibility(!window.document.hidden),
    false
  );
});
</script>
